<template>
  <div class="px-3 py-2 border-b border-white/5">
    <div class="relative">
      <!-- Positioned by the span: Element Plus' .el-icon sets position: relative
           and wins over Tailwind's `absolute`, which put the icon above the field. -->
      <span class="absolute left-2 top-1/2 -translate-y-1/2 flex text-text-dim pointer-events-none">
        <el-icon size="14"><Search /></el-icon>
      </span>
      <input
        ref="inputEl"
        :value="query"
        type="text"
        class="w-full pl-7 pr-8 py-1.5 rounded-md bg-black/30 border border-white/5 text-sm text-text-main placeholder:text-text-dim outline-none focus:border-primary/50 transition-colors"
        :placeholder="placeholder"
        @input="onInput"
        @keydown.escape="onEsc"
      />
      <button
        v-if="query"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-text-dim hover:text-white"
        @click="onClear"
      >
        <el-icon size="12"><Close /></el-icon>
      </button>
    </div>

    <div v-if="query && results.length > 0" class="mt-2 max-h-56 overflow-y-auto scrollbar-thin rounded-md bg-black/40 border border-white/5">
      <button
        v-for="u in results"
        :key="u.id"
        type="button"
        class="w-full flex items-center gap-2.5 px-2 py-2 hover:bg-white/[0.03] text-left"
        @click="selectUser(u)"
      >
        <img :src="u.avatar || '/images/default-avatar.svg'" :alt="u.name" class="w-7 h-7 rounded-full object-cover shrink-0" />
        <div class="min-w-0 flex-1">
          <p class="text-sm text-white truncate">{{ u.name }}</p>
          <p class="text-[10px] text-text-muted capitalize truncate">
            {{ u.role }}
            <span v-if="u.email" class="text-text-dim normal-case">· {{ u.email }}</span>
          </p>
        </div>
        <!-- Tenant badge only appears on superadmin cross-tenant searches —
             a concise way to disambiguate two users with the same name on
             different tenants. -->
        <span
          v-if="u.tenant"
          class="shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30"
          :title="'Tenant: ' + u.tenant.name"
        >
          {{ u.tenant.name }}
        </span>
      </button>
    </div>

    <div v-else-if="query && !loading" class="mt-2 text-[11px] text-text-dim italic px-1">
      No users found
    </div>
    <div v-else-if="loading" class="mt-2 text-[11px] text-text-dim px-1">
      Searching...
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Search, Close } from '@element-plus/icons-vue'
import { useChatUserSearch } from '~/composables/chat/useChatUserSearch'

const props = defineProps({
  placeholder: { type: String, default: 'Search users...' }
})
const emit = defineEmits(['select'])

const inputEl = ref(null)
const { query, results, loading, setQuery, reset } = useChatUserSearch()

function onInput(e) {
  setQuery(e.target.value)
}
function onClear() {
  reset()
  inputEl.value?.focus()
}
function onEsc() {
  if (query.value) { reset(); return }
  inputEl.value?.blur()
}
function selectUser(user) {
  emit('select', user)
  reset()
}
</script>
